import { Button } from "@/components/ui/button";
import { useState } from "react";
import { ReviewModal } from "@/lib/builder/modals/ReviewModal";
import { ChevronLeft, ChevronRight, MessageSquareDiff, Luggage } from "lucide-react";

type UserRole = "travel_designer" | "traveler" | "admin";

interface BuilderFooterProps {
  role?: UserRole;
  isReadOnly?: boolean;
  isLoading?: boolean;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  isRecapStep?: boolean;
  canProceed?: boolean;
  isSavingDraft?: boolean;
  totalPrice?: number;
  startDate?: string;
  endDate?: string;
  itineraryStatus?: string;
  onCancel?: () => void;
  onPreviousStep?: () => void;
  onNextStep?: () => void;
  onCreateQuote?: () => void;
  onRejectItinerary?: (note?: string) => void;
  onAcceptItinerary?: (note?: string) => void;
  onUpdateItineraryNote?: (note: string) => Promise<void>;
  onRequestChanges?: () => void;
  onBookTrip?: () => void;
}

const formatDateRange = (startDate?: string, endDate?: string): string | null => {
  if (!startDate || !endDate) return null;
  const start = new Date(startDate);
  const end = new Date(endDate);
  const startMonth = start.toLocaleString('en-US', { month: 'short' });
  const endMonth = end.toLocaleString('en-US', { month: 'short' });
  const startDay = start.getUTCDate();
  const endDay = end.getUTCDate();
  if (startMonth === endMonth) {
    return `${startMonth} ${startDay} - ${endDay}`;
  }
  return `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
};

export const BuilderFooter = ({
  role,
  isReadOnly = false,
  isLoading = false,
  isFirstStep = false,
  isLastStep = false,
  isRecapStep = false,
  canProceed = true,
  isSavingDraft = false,
  totalPrice,
  startDate,
  endDate,
  itineraryStatus,
  onCancel,
  onPreviousStep,
  onNextStep,
  onCreateQuote,
  onRejectItinerary,
  onAcceptItinerary,
  onUpdateItineraryNote,
  onRequestChanges,
  onBookTrip,
}: BuilderFooterProps) => {
  const [showReviewModal, setShowReviewModal] = useState(false);

  const isAdmin = role === "admin";
  const isTraveler = role === "traveler" || (isReadOnly);


  const leftButtons = [
    {
      label: "Request itinerary changes",
      icon: <MessageSquareDiff className="h-4 w-4" />,
      onClick: onRequestChanges,
      show: isTraveler && itineraryStatus !== 'completed' && onRequestChanges,
    },
    {
      label: "Exit",
      icon: <ChevronLeft className="h-4 w-4" />,
      onClick: onCancel,
      show: ((isTraveler && itineraryStatus === 'completed') || (!isTraveler && isFirstStep)) && onCancel,
    },
    {
      label: "Back",
      icon: <ChevronLeft className="h-4 w-4" />,
      onClick: onPreviousStep,
      show: !isTraveler && !isFirstStep && onPreviousStep,
    }
  ];

  const rightButtons = [
    {
      label: "Book your trip",
      icon: <Luggage className="h-4 w-4" />,
      onClick: onBookTrip,
      show: isTraveler && itineraryStatus !== 'completed' && itineraryStatus !== 'request_changes',
    },
    {
      label: "Paid",
      icon: (
        <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      ),
      onClick: undefined,
      show: isTraveler && itineraryStatus === 'completed',
      disabled: true,
      className: "rounded-2xl disabled:bg-green-600 px-3 py-2 readonly text-white font-semibold",
    },
    {
      label: "Complete review",
      icon: null,
      onClick: () => setShowReviewModal(true),
      show: isAdmin && isLastStep && itineraryStatus === 'pending',
    },
    {
      label: "Submit for approval",
      icon: null,
      onClick: onCreateQuote,
      show: !isTraveler && isLastStep && (
        itineraryStatus === 'draft' || itineraryStatus === 'request_changes' || 
        itineraryStatus === 'rejected' || itineraryStatus === 'approved'
      ),
    },
    {
      label: "Next",
      icon: <ChevronRight className="h-4 w-4" />,
      onClick: onNextStep,
      show: !isTraveler && !isLastStep,
    }
  ];

  if (isLoading) return null;
  if (isTraveler && itineraryStatus === 'request_changes') return null;

  return (
    <>
      <footer className="flex-shrink-0 border-t border-gray-200 px-8 py-4">
        <div className="flex items-center justify-between">
          {/* Left */}
          <div className="flex items-center gap-2">
            {leftButtons.filter(b => b.show).map((b, idx) => (
              <Button
                key={b.label + idx}
                size="sm"
                onClick={b.onClick}
                className={`flex items-center gap-2 ${b.className || ''}`}
                variant={b.label === "Exit" || b.label === 'Back' ? "ghost" : b.label === "Request itinerary changes" ? "outline" : undefined}
                disabled={b.disabled || (b.label === "Next" && (!canProceed || isSavingDraft))}
                tabIndex={b.disabled ? -1 : undefined}
              >
                {b.icon}
                {b.label}
              </Button>
            ))}
          </div>

          {/* Right */}
          <div className="flex items-center gap-2">
            {!!totalPrice && isTraveler && itineraryStatus === 'sended' && (
              <div className="text-right">
                <p className="text-sm font-semibold text-gray-900">
                  € {totalPrice.toLocaleString('it-IT')} total
                </p>
              </div>
            )}
            {rightButtons.filter(b => b.show).map((b, idx) => (
              <Button
                key={b.label + idx}
                size="sm"
                onClick={b.onClick}
                className={`flex items-center gap-2 ${b.className || ''}`}
                variant={b.label === "Exit" || b.label === 'Back' ? "ghost" : b.label === "Request itinerary changes" ? "outline" : undefined}
                disabled={b.disabled || (b.label === "Next" && (!canProceed || isSavingDraft))}
                tabIndex={b.disabled ? -1 : undefined}
              >
                {b.icon}
                {b.label}
              </Button>
            ))}
          </div>
        </div>
      </footer>
      {showReviewModal && (
        <ReviewModal
          isOpen={showReviewModal}
          onClose={() => setShowReviewModal(false)}
          onAccept={onAcceptItinerary}
          onReject={onRejectItinerary}
        />
      )}
    </>
  );
};
